import {
  type FlexibleConnectedPositionStrategy,
  type Overlay,
  type OverlayRef,
} from '@angular/cdk/overlay';
import { type BehaviorSubject } from 'rxjs';

import { type ColumnDataType, type FormatType } from '@blaze-shared/api-contracts-domain';
import { type ColumnType, type DatasetColumn } from '@blaze-shared/schema-domain';
import { isValidDate, mutationRxUtils } from '@blaze-shared/shared-util';

import {
  type CellEditMode,
  type CellValueUpdateFromTag,
  type CellValueUpdateModel,
  type CellValueUpdateModelForFile,
  type CellValueUpdateModelForTag,
} from '../../elements/table/inline-editor/base-editor/inline-editor.types';

const InlineEditModeColumnTypes: ColumnType[] = [
  'String',
  'GeoPoint',
  'Number',
  'Date',
  'DateTime',
  'Time',
];

const SwitchEditModeDataTypes: ColumnType[] = ['Boolean'];

export const MenuEditModeDataTypes: ColumnDataType[] = ['File', 'Tag', 'Link', 'Image'];
export const MenuEditModeColumnFormats: FormatType[] = ['TimeZone'];

export function isColumnValueInlineEditable(schema?: DatasetColumn): boolean {
  if (schema?.isComputed) {
    return false;
  }

  const result =
    schema?.format?.formatType !== 'TimeZone' &&
    !!schema?.columnType &&
    InlineEditModeColumnTypes.includes(schema.columnType);
  return result;
}

export function getEditMode(schema?: DatasetColumn): CellEditMode {
  if (schema?.isComputed) {
    return false;
  } else if (
    schema?.format?.formatType ||
    (schema?.columnType && InlineEditModeColumnTypes.includes(schema.columnType))
  ) {
    return 'inline-editor';
  } else if (
    (schema?.dataType && MenuEditModeDataTypes.includes(schema.dataType)) ||
    (schema?.format?.formatType && MenuEditModeColumnFormats.includes(schema.format.formatType))
  ) {
    return 'menu';
  } else if (schema?.columnType && SwitchEditModeDataTypes.includes(schema.columnType)) {
    return 'switch';
  }
  return false;
}

export function isCellUpdateModelForFile(val?: any): val is CellValueUpdateModelForFile {
  return (
    val && typeof val === 'object' && ['file', 'element', 'isSecure'].every((key) => key in val)
  );
}

export function isCellUpdateModelForTag(val?: any): val is CellValueUpdateModelForTag {
  return val && typeof val === 'object' && ['tag', 'element'].every((key) => key in val);
}

export function isCellUpdateFromTag(val?: any): val is CellValueUpdateFromTag {
  return val && typeof val === 'object' && ['actionType', 'tag'].every((key) => key in val);
}

export function isDateTimeEq(dateA?: string, dateB?: string): boolean {
  if (!dateA || !dateB) {
    return dateA === dateB;
  }

  const dateDateA = new Date(dateA);
  const dateDateB = new Date(dateB);

  if (isValidDate(dateDateA)) {
    return dateDateA.getTime() === dateDateB.getTime();
  }
  // else time
  const splittedDateA = dateA.split(':').map(Number);
  const splittedDateB = dateB.split(':').map(Number);

  // push missing seconds
  if (!splittedDateA[2]) {
    splittedDateA.push(0);
  }
  if (!splittedDateB[2]) {
    splittedDateB.push(0);
  }

  return splittedDateA.every((part, index) => part === splittedDateB[index]);
}

// #region editor overlay
export function generatePositionStrategyForOverlay(
  overlay: Overlay,
  cellEditInfo: CellValueUpdateModel,
  overlayPositionBelow?: boolean,
): FlexibleConnectedPositionStrategy | undefined {
  const relatedElement = cellEditInfo.detailData?.element ?? cellEditInfo.tableCell;

  if (!relatedElement) {
    return;
  }

  return overlay
    .position()
    .flexibleConnectedTo(relatedElement)
    .withPositions(
      overlayPositionBelow
        ? [{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]
        : [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }],
    )
    .withFlexibleDimensions(true)
    .withGrowAfterOpen(true)
    .withPush(true);
}

export function destroyOverlayRef(
  overlayRef: OverlayRef,
  cellEditInfo: Pick<CellValueUpdateModel, 'tableCell'>,
  cellEdit$$: BehaviorSubject<CellValueUpdateModel | undefined>,
): void {
  overlayRef?.detach();
  overlayRef?.dispose();
  // clear cell after dispatch to make cell clickable again
  if (cellEdit$$.value?.tableCell === cellEditInfo.tableCell) {
    cellEdit$$.next(undefined);
  }
}
// #endregion

export function findEditableElementHTML(
  currentCellEditInfo: CellValueUpdateModel | undefined,
  cellEditInfoToFindHTML: CellValueUpdateModel,
  hostElement: HTMLElement,
): HTMLElement | undefined | null {
  if (cellEditInfoToFindHTML.tableCell) {
    return cellEditInfoToFindHTML.tableCell;
  }

  if (!currentCellEditInfo) {
    return hostElement.querySelector(
      `be-table-row [data-col-name="${cellEditInfoToFindHTML.columnName}"][data-row-id="${cellEditInfoToFindHTML.rowNumber}"]`,
    ) as HTMLElement;
  }

  return cellEditInfoToFindHTML.rowNumber === currentCellEditInfo.rowNumber
    ? currentCellEditInfo.tableCell
        ?.closest('be-table-row')
        ?.querySelector(`[data-col-name="${currentCellEditInfo.columnName}"]`)
    : currentCellEditInfo.tableCell
        ?.closest('be-table-row')
        ?.nextElementSibling?.querySelector(
          `.cell[data-col-name="${currentCellEditInfo.columnName}"]`,
        );
}

export async function scrollCellIntoViewIfNeeded(
  cellEditInfo: CellValueUpdateModel | undefined,
  hostElement: HTMLElement,
): Promise<void> {
  if (!cellEditInfo) {
    return;
  }

  let element = cellEditInfo?.tableCell;

  if (element instanceof HTMLElement) {
    element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
    return;
  }

  if (!element) {
    const headerElement = hostElement
      .getElementsByTagName('be-table-header')[0]
      .querySelector(`[data-col-name="${cellEditInfo.columnName}"]`);
    const headerElementRect = headerElement?.getBoundingClientRect();
    const scrollContainer = headerElement?.closest('[blazevirtualscrollableelement]');
    const containerRect = scrollContainer?.getBoundingClientRect();
    const isHorizontallyInView =
      headerElementRect &&
      containerRect &&
      headerElementRect.left >= containerRect.left &&
      headerElementRect.right <= containerRect.right;
    if (headerElementRect && containerRect && !isHorizontallyInView) {
      const scrollValue = headerElementRect.left - containerRect.left;
      hostElement?.querySelector('[blazevirtualscrollableelement]')?.scrollBy(scrollValue, 0);
    }
    cellEditInfo.tableCell =
      findEditableElementHTML(undefined, cellEditInfo, hostElement) ?? undefined;
    if (cellEditInfo && !cellEditInfo?.tableCell) {
      await waitForElementToExist(cellEditInfo);
      cellEditInfo.tableCell =
        findEditableElementHTML(undefined, cellEditInfo, hostElement) ?? undefined;
    }
    element = cellEditInfo.tableCell;
  }

  if (!element) {
    return;
  }

  const scrollContainer = element.closest('[blazevirtualscrollableelement]');

  if (!scrollContainer) {
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const isVerticallyInView =
    // element visible in container
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    // element visible on the screen
    elementRect.top >= 0 &&
    elementRect.bottom <= viewportHeight;
  const isHorizontallyInView =
    //element visible in container
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right &&
    //element visible on the screen
    elementRect.left >= 0 &&
    elementRect.right <= viewportWidth;

  if (!isVerticallyInView || !isHorizontallyInView) {
    element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
  }
}

export function waitForElementToExist(selector: CellValueUpdateModel): Promise<HTMLElement> {
  return mutationRxUtils.fromExistInDomObserver(
    `[data-row-id="${selector.rowNumber}"][data-col-name="${selector.columnName}"]`,
  );
}
